import { AppLanguage, getLanguageStrings } from "internationalisation";
import { assertNever } from "../helper";
import { RespondentConfigBuilder } from "../respondentConfigBuilder";
import { RespondentTask, WebPlatform } from "../types";
import { generateBvodTask, generateExternalSurveyTask, generateUploadsTask, generateWebPlatformTask } from "../tasks";

export const MAAU0625CollGroupIds = [
	"MAAU-453-0625-YOUTUBE",
	"MAAU-454-0625-TIKTOK",
	"MAAU-455-0625-INSTAGRAM",
	"MAAU-456-0625-PODCAST-1",
	"MAAU-457-0625-PODCAST-2"
] as const;

export type MAAU0625CollGroupId = typeof MAAU0625CollGroupIds[number];

type MAAU0625CollConfig = {
	groupId: MAAU0625CollGroupId,
	metaFieldNames: ReadonlyArray<string>
}

export const MAAU0625CollYTWebPlatformIds = [
	"MAAU-0625-YOUTUBE-1",
	"MAAU-0625-YOUTUBE-2",
	"MAAU-0625-YOUTUBE-3",
] as const;

export const MAAU0625CollWebPlatformIds = [
	"MAAU-0625-TIKTOK",
	"MAAU-0625-INSTAGRAM",
	...MAAU0625CollYTWebPlatformIds
] as const;

export type MAAU0625CollWebPlatformId = typeof MAAU0625CollWebPlatformIds[number];

const getWPTaskLengthMinutes = (webPlatformId: MAAU0625CollWebPlatformId): string => {
	switch(webPlatformId) {
		case "MAAU-0625-YOUTUBE-1":
		case "MAAU-0625-YOUTUBE-3":
			return "6";
		
		case "MAAU-0625-YOUTUBE-2":
			return "5";

		case "MAAU-0625-TIKTOK":
		case "MAAU-0625-INSTAGRAM":
			return "10";
		
		default:
			assertNever(webPlatformId);
	}
}

const getWebPlatformConfigUrl = (webPlatformId: MAAU0625CollWebPlatformId): string => {
	return `https://atrace-cdn.amplifiedintelligence.io/WebPlatform/Configs/MAAU-0625/${webPlatformId}.json`
}

export const MAAU0625CollBvodIds = [
	"MAAU-0625-PODCAST-1",
	"MAAU-0625-PODCAST-2"
] as const;

export type MAAU0625CollBvodId = typeof MAAU0625CollBvodIds[number];

const getBvodTaskLengthMinutes = (bvodId: MAAU0625CollBvodId): string => {
	switch(bvodId) {
		case "MAAU-0625-PODCAST-1":
		case "MAAU-0625-PODCAST-2":
			return "22";
		
		default:
			assertNever(bvodId);
	}
}

const getChannelConfigUrl = (bovdId: MAAU0625CollBvodId): string => {
	return `https://atrace-cdn.s3.ap-southeast-2.amazonaws.com/BvodChannels/ChannelLists/${bovdId}.json`;
}

const getWebPlatform = (webPlatformId: MAAU0625CollWebPlatformId): WebPlatform => {
	switch(webPlatformId) {
		case "MAAU-0625-YOUTUBE-1":
		case "MAAU-0625-YOUTUBE-2":
		case "MAAU-0625-YOUTUBE-3":
			return "Youtube";

		case "MAAU-0625-TIKTOK":
			return "TikTok";
		
		case "MAAU-0625-INSTAGRAM":
			return "Instagram";

		default: assertNever(webPlatformId);
	}
}

const getSurveyUrl = (groupId: MAAU0625CollGroupId): string => {
	let surveyId: string;
	switch (groupId) {
		case "MAAU-453-0625-YOUTUBE":
			surveyId = "SV_ahH4mGRrdpIDWHc";
			break;
		
		case "MAAU-454-0625-TIKTOK":
			surveyId = "SV_802XzUGJ2BeqifI";
			break;

		case "MAAU-455-0625-INSTAGRAM":
			surveyId = "SV_b8Icr05vWlJ9jdI";
			break;
		
		case "MAAU-456-0625-PODCAST-1":
		case "MAAU-457-0625-PODCAST-2":
			surveyId = "SV_5BzNPGYCXzS0u9w";
			break;
		
		default:
			assertNever(groupId);
	}

	return `https://survey.au1.qualtrics.com/jfe/form/${surveyId}`;
}

const getMainTasks = (groupId: MAAU0625CollGroupId): RespondentTask[] => {
	switch(groupId) {
		case "MAAU-453-0625-YOUTUBE":
			let tasks: Array<RespondentTask> = [];

			MAAU0625CollYTWebPlatformIds.forEach(id => {
				tasks.push(
					generateWebPlatformTask({
						groupId: groupId,
						platform: getWebPlatform(id),
						webPlatformConfigUrl: getWebPlatformConfigUrl(id),
						webPlatformID: id,
						taskLengthMinutes: getWPTaskLengthMinutes(id),
						useWebPlatformIDForTaskID: true,
						langStrings: getLanguageStrings(AppLanguage.en)
					})
				);
			});

			return tasks;

		case "MAAU-454-0625-TIKTOK":
			const tiktokId: MAAU0625CollWebPlatformId = "MAAU-0625-TIKTOK";
			return [
				generateWebPlatformTask({
					groupId: groupId,
					platform: getWebPlatform(tiktokId),
					webPlatformConfigUrl: getWebPlatformConfigUrl(tiktokId),
					webPlatformID: tiktokId,
					taskLengthMinutes: getWPTaskLengthMinutes(tiktokId),
					useWebPlatformIDForTaskID: true,
					langStrings: getLanguageStrings(AppLanguage.en)
				})
			];

		case "MAAU-455-0625-INSTAGRAM":
			const instagramId: MAAU0625CollWebPlatformId = "MAAU-0625-INSTAGRAM";
			return [
				generateWebPlatformTask({
					groupId: groupId,
					platform: getWebPlatform(instagramId),
					webPlatformConfigUrl: getWebPlatformConfigUrl(instagramId),
					webPlatformID: instagramId,
					taskLengthMinutes: getWPTaskLengthMinutes(instagramId),
					useWebPlatformIDForTaskID: true,
					langStrings: getLanguageStrings(AppLanguage.en)
				})
			];

		case "MAAU-456-0625-PODCAST-1":
			const podcast1Id: MAAU0625CollBvodId = "MAAU-0625-PODCAST-1";
			return [
				generateBvodTask({
					taskID: podcast1Id,
					channelsJsonUrl: getChannelConfigUrl(podcast1Id),
					isTv: false,
					langStrings: getLanguageStrings(AppLanguage.en),
					isRadio: false,
					title: "PODCASTS"
				})
			];

		case "MAAU-457-0625-PODCAST-2":
			const podcast2Id: MAAU0625CollBvodId = "MAAU-0625-PODCAST-2";
			return [
				generateBvodTask({
					taskID: podcast2Id,
					channelsJsonUrl: getChannelConfigUrl(podcast2Id),
					isTv: false,
					langStrings: getLanguageStrings(AppLanguage.en),
					isRadio: false,
					title: "PODCASTS"
				})
			];
	}
}

export const addMAAU0625CollConfig = (
	config: MAAU0625CollConfig,
	builder: RespondentConfigBuilder
): RespondentConfigBuilder => {

	const FINISH_MESSAGE = "Congratulations!\nYou have now completed all required tasks and your data has been uploaded. Your incentive has been processed. Thank you for your time.";
  const langStrings = getLanguageStrings(AppLanguage.en);

	const externalSurveyTask = generateExternalSurveyTask({
		title: langStrings.Common.survey,
		description: langStrings.Survey.description,
		extraMetaFields: config.metaFieldNames,
		taskID: `${config.groupId}-SURVEY`,
		surveyUrl: getSurveyUrl(config.groupId)
	});

	const uploadTask = generateUploadsTask({
		config: {
			title: "Uploads"
		},
		taskId: `${config.groupId}-UPLOADS`
	});

	builder.setFlag('deviceLock', 'mobile');
	builder.setFlag('requireMinimumBattery', 0.3);
	builder.setFlag('finishMessageOverride', FINISH_MESSAGE);
	builder.setFlag('preventTaskProgress', false);
	
	const mainTasks = getMainTasks(config.groupId);
	mainTasks.forEach(task => builder.addTask(task));

	builder.addTask(externalSurveyTask);
	builder.addTask(uploadTask);
	return builder;
}

